/**
 * CheckboxField — una casilla con su rótulo y su descripción.
 *
 * ⚠️ El `<input>` real va **oculto con `.cl-sr-only`, no con `display: none`**.
 * La diferencia importa: un input en `display:none` sale del orden de
 * tabulación y del árbol de accesibilidad, así que la casilla dejaría de
 * poder marcarse con el teclado y el lector de pantalla no la vería. Con
 * `sr-only` está ahí, invisible, y el `<span>` que sí se ve solo lo refleja.
 *
 * ⚠️ Todo el bloque es el `<label>`, así que el área de toque es la etiqueta
 * entera y no el cuadrito de 15px. Es lo que hace que la casilla cumpla el
 * mínimo táctil sin agrandar el cuadrito.
 */
import { useId } from 'react'
import type { ComponentPropsWithRef, CSSProperties, LabelHTMLAttributes, ReactNode } from 'react'

export interface CheckboxFieldProps
  extends Omit<LabelHTMLAttributes<HTMLLabelElement>, 'className' | 'style' | 'onChange'> {
  label: ReactNode
  description?: ReactNode
  checked?: boolean
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  disabled?: boolean
  id?: string
  name?: string
  className?: string
  style?: CSSProperties
  /**
   * Lo que va al `<input type="checkbox">` y no al `<label>`.
   *
   * 🔴 Es por donde entra `react-hook-form`: `inputProps={register('activo')}`.
   *
   * 🟢 Y acá el tilde sigue funcionando sin que el componente sepa el estado,
   * porque el CSS lo deriva del input real
   * (`.cl-check input:checked + .cl-check__box`). La prop `checked` y su
   * `data-checked` son para el uso controlado; con `register` manda el DOM.
   */
  inputProps?: Omit<ComponentPropsWithRef<'input'>, 'id' | 'type'>
}

export function CheckboxField({
  label,
  description,
  checked = false,
  onChange,
  disabled = false,
  id: idProp,
  name,
  className = '',
  style,
  inputProps,
  ...rest
}: CheckboxFieldProps) {
  const auto = useId()
  const id = idProp || `cl-${auto}`

  return (
    <label
      className={`cl-check ${className}`.trim()}
      htmlFor={id}
      style={{
        ...(disabled ? { opacity: 0.55, cursor: 'not-allowed' } : null),
        ...style,
      }}
      {...rest}
    >
      <input
        id={id}
        name={name}
        type="checkbox"
        className="cl-sr-only"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        {...inputProps}
      />
      <span className="cl-check__box" data-checked={checked || undefined} aria-hidden="true">
        ✓
      </span>
      <span style={{ minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 12, fontWeight: 600 }}>{label}</span>
        {description && (
          <span
            style={{
              display: 'block',
              fontSize: 10.5,
              color: 'rgb(var(--color-content-subtle))',
              marginTop: 2,
              lineHeight: 1.4,
            }}
          >
            {description}
          </span>
        )}
      </span>
    </label>
  )
}
